import React, { useEffect, useState, useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { FaChartLine, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const LineChartComponent = ({ applicationData }) => {
    const [lineChartData, setLineChartData] = useState({
        series: [],
        xAxis: [],
    });
    const [selectedMetric, setSelectedMetric] = useState('total_views');
    const [years, setYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);

    const allMonths = useMemo(() => {
        if (!applicationData || !applicationData.barchartdata) {
            return [];
        }
        const months = new Set();
        Object.values(applicationData.barchartdata).forEach(platformData => {
            Object.keys(platformData).forEach(month => {
                months.add(month);
            });
        });
        return Array.from(months).sort();
    }, [applicationData]);

    useEffect(() => {
        const uniqueYears = [...new Set(allMonths.map(month => month.split('-')[0]))].sort();
        setYears(uniqueYears);
        if (uniqueYears.length > 0 && selectedYear === null) {
            setSelectedYear(uniqueYears[uniqueYears.length - 1]);
        }
    }, [allMonths, selectedYear]);

    useEffect(() => {
        if (!applicationData || !applicationData.barchartdata || !selectedYear) {
            setLineChartData({ series: [], xAxis: [] });
            return;
        }

        const monthsForYear = allMonths.filter(month => month.startsWith(selectedYear));

        const series = Object.entries(applicationData.barchartdata).map(([platformName, platformData]) => ({
            label: platformName,
            data: monthsForYear.map(month => platformData[month] ? platformData[month][selectedMetric] : 0),
            valueFormatter: (value) => selectedMetric === 'total_royalty' ? `₹${value.toLocaleString()}` : value.toLocaleString(),
        }));

        setLineChartData({
            series: series,
            xAxis: [{
                data: monthsForYear.map(m => new Date(m + '-02').toLocaleString('default', { month: 'short' })), // Display month names
                scaleType: 'band',
            }],
        });
    }, [applicationData, selectedMetric, selectedYear, allMonths]);

    const handlePrevYear = () => {
        const currentIndex = years.indexOf(selectedYear);
        if (currentIndex > 0) {
            setSelectedYear(years[currentIndex - 1]);
        }
    };

    const handleNextYear = () => {
        const currentIndex = years.indexOf(selectedYear);
        if (currentIndex < years.length - 1) {
            setSelectedYear(years[currentIndex + 1]);
        }
    };


    if (lineChartData.series.length === 0) {
        return <div className="bg-gradient-card border border-border/50 rounded-md p-4">Loading...</div>;
    }

    return (
        <div className="bg-gradient-card border border-border/50 rounded-md p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                    <FaChartLine className="inline-block mr-2 text-purple-400" /> Monthly Growth
                </h3>
                <div className="flex items-center">
                    <button onClick={handlePrevYear} disabled={years.indexOf(selectedYear) === 0} className="p-2 rounded-full bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        <FaArrowLeft />
                    </button>
                    <span className="mx-4 text-lg font-semibold">{selectedYear}</span>
                    <button onClick={handleNextYear} disabled={years.indexOf(selectedYear) === years.length - 1} className="p-2 rounded-full bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        <FaArrowRight />
                    </button>
                </div>
                <select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    className="bg-primary text-primary-foreground font-bold rounded-md py-2 px-4"
                >
                    <option value="total_views">Total Views</option>
                    <option value="total_royalty">Total Royalty</option>
                    <option value="total_songs">Total Songs</option>
                </select>
            </div>
            <LineChart
                series={lineChartData.series}
                xAxis={lineChartData.xAxis}
                height={300}
                grid={{ vertical: true, horizontal: true }} // Ensure grid is enabled for styling
                sx={{
                    // X-axis styling
                    '.MuiChartsAxis-bottom .MuiChartsAxis-line': {
                        stroke: '#FFFFFF',
                    },
                    '.MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                        fill: '#FFFFFF',
                    },
                    // Y-axis styling
                    '.MuiChartsAxis-left .MuiChartsAxis-line': {
                        stroke: '#FFFFFF',
                    },
                    '.MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                        fill: '#FFFFFF',
                    },
                    // Axis title styling (if any are present)
                    '.MuiChartsAxis-title': {
                        fill: '#FFFFFF',
                    },
                    // Legend label styling
                    '.MuiChartsLegend-label': {
                        fill: '#FFFFFF',
                        color: '#FFFFFF',
                    },
                    // Grid line styling
                    '.MuiChartsGrid-horizontal': {
                        stroke: 'rgba(255, 255, 255, 0.3)', // Lighter white for visibility against black background
                        strokeDasharray: '3 3',
                    },
                    '.MuiChartsGrid-vertical': {
                        stroke: 'rgba(255, 255, 255, 0.3)', // Lighter white for visibility against black background
                        strokeDasharray: '3 3',
                    },
                    // Tooltip styling (may require more specific targeting or slotProps for full effect)
                    '.MuiChartsTooltip-root': {
                        color: '#FFFFFF',
                        backgroundColor: '#333333',
                    },
                    '.MuiChartsTooltip-mark': { // Tooltip marker
                        stroke: '#FFFFFF',
                        fill: '#FFFFFF',
                    },
                    // Chart background
                    '.MuiChartsPlot-background': {
                        fill: 'transparent', // Assuming a dark background, make chart background transparent
                    },
                    // Series element styling (example for points, lines usually take color from series prop)
                    '.MuiChartsLine-mark': { // Points on the line
                        stroke: '#FFFFFF',
                        fill: '#FFFFFF',
                    },
                    // Area styling (if series has `area: true`)
                    '.MuiChartsArea-root': {
                        fill: 'rgba(255, 255, 255, 0.1)', // Light white transparent fill
                    }
                }}
                legend={{ direction: 'row', position: { vertical: 'top', horizontal: 'right' }, padding: 0 }}
            />
        </div>
    );
};

export default LineChartComponent;